import { List, ListItem, Stack, Typography } from '@mui/joy';
import { CheckCircle, Warning } from '@phosphor-icons/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Confirmation } from 'core/components';
import { renderHtmlMessage } from 'core/utils';
import { PricingHelper } from 'pricing/helpers';
import { useCostDecimals } from 'pricing/hooks';
import { IApplySelectedSpaJobResult, ICustomerQuotePart } from 'quotes/types';
import { Color, Level } from 'theme/enums';

import { LineLink } from './LineLink';
import {
  getApplySelectedSpaPartNumbers,
  getApplySelectedSpaSkippedLineMessage,
  getApplySelectedSpaSummary,
  groupSkippedLinesByReason,
} from '../applyToLines';

const getTitleKey = (isStopped: boolean, hasApplied: boolean): string => {
  if (isStopped) {
    return 'titleSelectedSpaApplyStopped';
  }

  return hasApplied ? 'titleSelectedSpaApplyDone' : 'titleSelectedSpaApplyResult';
};

const getOptionStateKey = (isReplaced: boolean): string =>
  isReplaced ? 'labelSelectedSpaApplyOptionReplaced' : 'labelSelectedSpaApplyOptionKept';

interface IProps {
  result: IApplySelectedSpaJobResult | null;
  // The quote grid's rows: the only source of a skipped line's part number.
  gridRows?: ICustomerQuotePart[];
  agreementCode?: string | null;
  onLineClick: (quotePartId: number) => void;
  onClose: () => void;
  // The job stopped midway: the result is what it did before it stopped.
  isStopped?: boolean;
}

export const ApplySelectedSpaResultDialog = ({
  result,
  gridRows,
  agreementCode,
  onLineClick,
  onClose,
  isStopped = false,
}: IProps) => {
  const { t } = useTranslation();

  const costDecimals = useCostDecimals();

  // Only while open: the host re-renders on every keystroke in its drawer.
  const partNumbers = useMemo(
    () => (result ? getApplySelectedSpaPartNumbers(gridRows, result.quoteParts) : {}),
    [gridRows, result]
  );

  const formatCost = (amount: number | null) =>
    PricingHelper.formatPricing({ amount, decimals: costDecimals, fixedDecimals: true });

  const applied = result?.applied ?? [];
  const skippedCount = result?.skipped?.length ?? 0;
  const skippedGroups = groupSkippedLinesByReason(result?.skipped);
  const hasApplied = applied.length > 0;
  const isSuccess = hasApplied && !isStopped;

  const summary = getApplySelectedSpaSummary(result, {
    agreementCode: result?.spaCostSource || agreementCode,
    isStopped,
  });

  return (
    <Confirmation
      isOpen={!!result}
      color={isSuccess ? Color.SUCCESS : Color.DANGER}
      title={t(getTitleKey(isStopped, hasApplied))}
      icon={isSuccess ? <CheckCircle weight="fill" /> : undefined}
      message={
        <Stack spacing={2}>
          {renderHtmlMessage(t(summary.key, summary.values))}

          {result?.options && (
            // A div: each rendered message is a block of its own, which a paragraph cannot hold.
            <Typography level={Level.BODY_SM} component="div">
              {renderHtmlMessage(
                t('messageSelectedSpaApplyOptionManualCosts', {
                  outcome: t(getOptionStateKey(result.options.replaceManualCosts)),
                })
              )}
              {renderHtmlMessage(
                t('messageSelectedSpaApplyOptionSelectedSpas', {
                  outcome: t(getOptionStateKey(result.options.replaceSelectedSpas)),
                })
              )}
            </Typography>
          )}

          {hasApplied && (
            <Stack>
              <Typography
                level={Level.TITLE_SM}
                color={Color.SUCCESS}
                startDecorator={<CheckCircle weight="fill" />}
              >
                {t('labelSelectedSpaAppliedLines', { count: applied.length })}
              </Typography>
              <List marker="disc">
                {applied.map((line) => (
                  <ListItem key={line.quotePartId}>
                    <Stack>
                      <LineLink
                        line={line}
                        partNumber={partNumbers[line.quotePartId]}
                        onLineClick={onLineClick}
                      />
                      {renderHtmlMessage(
                        t('messageSelectedSpaAppliedCost', {
                          previousCost: formatCost(line.previousCost),
                          newCost: formatCost(line.newCost),
                        })
                      )}
                    </Stack>
                  </ListItem>
                ))}
              </List>
            </Stack>
          )}

          {skippedGroups.length > 0 && (
            <Stack spacing={1}>
              <Typography level={Level.TITLE_SM} color={Color.WARNING}>
                {t('labelSelectedSpaSkippedLines', { count: skippedCount })}
              </Typography>
              {skippedGroups.map(({ labelKey, lines }) => (
                <Stack key={labelKey}>
                  <Typography
                    level={Level.BODY_SM}
                    color={Color.WARNING}
                    startDecorator={<Warning weight="fill" size="1em" />}
                  >
                    {t(labelKey)}
                  </Typography>
                  <List marker="disc">
                    {lines.map((line) => {
                      const lineMessage = getApplySelectedSpaSkippedLineMessage(line, t(labelKey));

                      return (
                        <ListItem key={line.quotePartId}>
                          <Stack>
                            <LineLink
                              line={line}
                              partNumber={partNumbers[line.quotePartId]}
                              onLineClick={onLineClick}
                            />
                            {lineMessage && (
                              <Typography level={Level.BODY_SM} sx={{ color: 'text.tertiary' }}>
                                {lineMessage}
                              </Typography>
                            )}
                          </Stack>
                        </ListItem>
                      );
                    })}
                  </List>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      }
      cancelBtnLabel={t('buttonLabelClose')}
      onCancel={onClose}
    />
  );
};
